import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { useApi } from "@/core/api";
import {
  buildOptimisticCell,
  findSheetResponseCell,
  patchSpreadsheetSheetCells,
} from "@/payroll/ai-script/spreadsheet-cache";
import {
  aiScriptSpreadsheetSheetKey,
  aiScriptSpreadsheetSheetListKey,
} from "@/payroll/ai-script/spreadsheet-sheet-query";
import type {
  AiSpreadsheetCellKind,
  AiSpreadsheetValueType,
  IAiSpreadsheetCell,
  IAiSpreadsheetSheetResponse,
} from "@/payroll/ai-script/types";
import {
  WorkbookCellEditBatchError,
  enqueueWorkbookCellEdit,
  flushWorkbookCellEdits,
  hasPendingWorkbookCellEdits,
} from "@/payroll/ai-script/workbook-cell-edit-batcher";
import {
  isRecoverableWorkbookOperationError,
  workbookMutationRequestConfig,
} from "@/payroll/ai-script/workbook-mutation";
import { postAiScriptSpreadsheetPaste } from "./bulk-paste";
import { getAiScriptSpreadsheetCellPath } from "./paths";
import {
  clearSavedManualRowCellDrafts,
  executeWorkbookMutation,
} from "./workbook-refresh";

interface UpdateAiSpreadsheetCellParamsBase {
  runId: string;
  sheetId: string;
  cellId: string;
  rawInput: string;
  kind?: AiSpreadsheetCellKind;
}

interface UpdateAiSpreadsheetCellParams
  extends UpdateAiSpreadsheetCellParamsBase {
  valueType?: AiSpreadsheetValueType;
  baseVersion?: number;
}

export const buildUpdateAiSpreadsheetCellBody = ({
  rawInput,
  kind,
  valueType,
  baseVersion,
}: Pick<
  UpdateAiSpreadsheetCellParams,
  "rawInput" | "kind" | "valueType" | "baseVersion"
>) => ({
  raw_input: rawInput,
  ...(kind ? { kind } : {}),
  ...(valueType ? { value_type: valueType } : {}),
  ...(baseVersion != null ? { base_version: baseVersion } : {}),
});

const optimisticallyUpdateAiSpreadsheetCell = async (
  queryClient: QueryClient,
  businessId: string | null,
  {
    runId,
    sheetId,
    cellId,
    rawInput,
    kind,
    valueType,
  }: UpdateAiSpreadsheetCellParams,
) => {
  const sheetListKey = aiScriptSpreadsheetSheetListKey(businessId, runId);

  await queryClient.cancelQueries({ queryKey: sheetListKey });

  const loadedSheets = queryClient.getQueriesData<IAiSpreadsheetSheetResponse>({
    queryKey: sheetListKey,
  });
  const existingCell = loadedSheets.reduce<IAiSpreadsheetCell | undefined>(
    (found, [, data]) => found ?? findSheetResponseCell(data, cellId),
    undefined,
  );

  if (existingCell) {
    const optimisticCell = buildOptimisticCell(
      existingCell,
      rawInput,
      valueType,
      kind,
    );
    queryClient.setQueriesData<IAiSpreadsheetSheetResponse>(
      { queryKey: sheetListKey },
      (data) => patchSpreadsheetSheetCells(data, [optimisticCell]),
    );
  }

  return {
    runId,
    sheetKey: aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
  };
};

const handleAiSpreadsheetCellUpdateError = (
  queryClient: QueryClient,
  businessId: string | null,
  error: unknown,
  context:
    | Awaited<ReturnType<typeof optimisticallyUpdateAiSpreadsheetCell>>
    | undefined,
) => {
  const operationError =
    error instanceof WorkbookCellEditBatchError ? error.cause : error;
  if (isRecoverableWorkbookOperationError(operationError)) return;
  if (!businessId || !context) return;

  void flushWorkbookCellEdits(businessId, context.runId).then(() =>
    queryClient.invalidateQueries({
      queryKey: context.sheetKey,
      refetchType: "all",
    }),
  );
};

export const useUpdateAiSpreadsheetCell = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationFn: ({
      runId,
      sheetId,
      cellId,
      rawInput,
      kind,
    }: UpdateAiSpreadsheetCellParamsBase) => {
      if (!businessId) throw new Error("Business is required");

      return enqueueWorkbookCellEdit({
        businessId,
        runId,
        sheetId,
        edit: {
          cell_id: cellId,
          raw_input: rawInput,
          ...(kind ? { kind } : {}),
        },
        execute: (batchSheetId, edits) =>
          executeWorkbookMutation({
            api,
            queryClient,
            businessId,
            runId,
            refreshMode: "cell-edit",
            immediateSheetId: batchSheetId,
            onApplied: () =>
              clearSavedManualRowCellDrafts(
                queryClient,
                businessId,
                runId,
                batchSheetId,
                edits.map((edit) => edit.cell_id),
              ),
            flushPendingCellEdits: false,
            hasPendingClientBatch: () =>
              hasPendingWorkbookCellEdits(businessId, runId),
            submit: (idempotencyKey) =>
              postAiScriptSpreadsheetPaste(
                api,
                businessId,
                {
                  runId,
                  sheetId: batchSheetId,
                  edits,
                  rows: [],
                },
                idempotencyKey,
              ),
          }),
        report: (metrics) =>
          posthog?.capture("workbook_cell_edit_batch_completed", {
            batch_size: metrics.batchSize,
            operation_duration_ms: Math.round(metrics.operationDurationMs),
            queue_duration_ms: Math.round(metrics.queueDurationMs),
            succeeded: metrics.succeeded,
          }),
        onError: (error) =>
          handleAiSpreadsheetCellUpdateError(queryClient, businessId, error, {
            runId,
            sheetKey: aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
          }),
      });
    },
    onMutate: (params) =>
      optimisticallyUpdateAiSpreadsheetCell(queryClient, businessId, params),
  });
};

export const useDirectUpdateAiSpreadsheetCell = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      sheetId,
      cellId,
      rawInput,
      kind,
      valueType,
      baseVersion,
    }: UpdateAiSpreadsheetCellParams) => {
      if (!businessId) throw new Error("Business is required");

      const body = buildUpdateAiSpreadsheetCellBody({
        rawInput,
        kind,
        valueType,
        baseVersion,
      });
      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        onApplied: () =>
          clearSavedManualRowCellDrafts(
            queryClient,
            businessId,
            runId,
            sheetId,
            [cellId],
          ),
        submit: (idempotencyKey) =>
          api
            .patch(
              getAiScriptSpreadsheetCellPath(businessId, runId, cellId),
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onMutate: (params) =>
      optimisticallyUpdateAiSpreadsheetCell(queryClient, businessId, params),
    onError: (error, _variables, context) =>
      handleAiSpreadsheetCellUpdateError(
        queryClient,
        businessId,
        error,
        context,
      ),
  });
};
